import { Fragment, useEffect, useRef, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import {
  addDays,
  formatLongDate,
  weekdayName,
  type ISODate,
  type JournalEntry,
} from '@app/shared'

import {
  useClock,
  useJournalDayContext,
  useJournalEntry,
  useJournalStats,
  useSaveJournalEntry,
  useVoiceMemos,
  type JournalVoiceMemo,
} from '../lib/data'
import { DataModeBadge, HeaderBand } from '../components/HeaderBand'
import { Pencil } from '../components/icons'
import { ChevronLeft, ChevronRight } from '../components/journal/icons'
import { MoodDots } from '../components/journal/DayCards'
// The "+ tag" chip, the same component the Today composer uses. Its own import line, the
// convention this file's import block already keeps for parallel lanes.
import { AddTagChip } from '../components/journal/AddTagChip'
import { DayMemo, MemoAudioNote } from '../components/journal/DayMemo'
import { NextDayCard, ThatDay, Threads } from '../components/journal/ThatDay'
import { OnThisDay } from '../components/journal/IndexPanels'
import {
  dayOfMonth,
  formatShortDateWithYear,
  paragraphsOf,
  partOfDay,
} from '../components/journal/util'
// The durable draft (JRN-1d) — the hook the Today card and the phone composer already use, so
// this page cannot drift about what 'not saved' means. Its own import line, this file's habit.
import { unsavedSentence, useJournalDraft } from '../lib/use-journal-draft'
import { clockFromTimestamp } from '../lib/time'

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

/**
 * JOURNAL DAY PAGE — docs/design/handoff/"LifeOS Journal.dc.html", option 5b.
 *
 * One day, fully opened: the answered prompt, the entry text, voice memos rendered INLINE at the
 * position they were dropped, and a read-only "that day" strip on the right showing what else the
 * system knows about the date. Prev/next walk the calendar, not the entries — the day between two
 * entries is a real day and it opens.
 *
 * THIS PORT SHIPS THE READER. The mockup's caption says "Click anywhere in the text to keep
 * writing"; in-place editing of a past day is not built here, and pretending otherwise with a
 * contenteditable would put the undo contract (docs/design/FIDELITY.md → "What no gate can see")
 * on a surface nobody has hand-checked. The footer says so, and points at the one place writing
 * does happen.
 */
export function JournalDay() {
  const { date = '' } = useParams()
  const { data: clock } = useClock()

  // A malformed date is a typo in the address bar, not a state to design. Send it home.
  if (!ISO_DATE.test(date)) return <Navigate to="/journal" replace />

  return <Day date={date as ISODate} today={clock?.date} />
}

function Day({ date, today }: { date: ISODate; today: ISODate | undefined }) {
  /**
   * THE WRITING LATCH, and it lives HERE rather than in `NoEntry` on purpose.
   *
   * The first autosave gives this day text, which flips the branch below from `NoEntry` to
   * `EntryBody` — unmounting anything `NoEntry` was holding. A latch inside it therefore died at
   * the moment it was most needed, taking the editor out from under the cursor mid-sentence. Its
   * own smoke test caught that. It has to outlive the branch it controls.
   */
  const [writing, setWriting] = useState(false)
  const isToday = today !== undefined && date === today

  const { data: entry, isPending, error } = useJournalEntry(date)
  const { data: memos, error: memosError } = useVoiceMemos(date)
  const { data: context, error: contextError, isPending: contextPending } = useJournalDayContext(date)
  const { data: stats, error: statsError } = useJournalStats()

  const previous = addDays(date, -1)
  // The archive stops at today. There is no "tomorrow" to read, so there is no button offering one.
  const next = today && date < today ? addDays(date, 1) : null

  return (
    <>
      <HeaderBand band="journal" tall>
        <div className="flex flex-none items-center gap-3 whitespace-nowrap">
          <Link
            to="/journal"
            className="text-[12px] leading-none text-muted transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:text-ink"
          >
            Journal
          </Link>
          <span className="text-[12px] leading-none text-faintest" aria-hidden>
            /
          </span>
          <span className="text-[12px] font-medium leading-none text-ink-2">
            {formatShortDateWithYear(date)}
          </span>
          <SavedChip entry={entry} />
        </div>

        <DataModeBadge />

        <div className="flex flex-none items-center gap-2">
          <StepLink to={`/journal/${previous}`} label={stepLabel(previous)} direction="back" />
          {next ? (
            <StepLink to={`/journal/${next}`} label={stepLabel(next)} direction="forward" />
          ) : (
            <span
              title="This is the newest day there is."
              className="flex items-center gap-2 rounded-button border border-line bg-surface px-3 py-[7px] text-[11.5px] font-medium leading-none text-faint"
            >
              Today
              <ChevronRight size={13} className="text-faint" />
            </span>
          )}
        </div>
      </HeaderBand>

      <div className="flex min-h-0 flex-1 gap-4 px-[30px] pb-[26px]">
        <article className="flex min-w-0 flex-1 flex-col overflow-y-auto rounded-composer border border-line bg-surface px-9 pb-6 pt-[30px] shadow-lifted">
          {error ? (
            <ReadFailure date={date} message={error instanceof Error ? error.message : ''} />
          ) : isPending ? (
            <EntrySkeleton />
          ) : writing && isToday ? (
            <DayComposer date={date} initial={entry?.text ?? ''} />
          ) : !entry || entry.text.trim().length === 0 ? (
            <NoEntry date={date} isToday={isToday} hasClock={today !== undefined} onWrite={() => setWriting(true)} />
          ) : (
            <EntryBody entry={entry} memos={memos} memosError={memosError} />
          )}
        </article>

        <aside className="flex w-[326px] flex-none flex-col gap-[10px] overflow-y-auto">
          <ThatDay context={context} error={contextError} isPending={contextPending} />
          {entry && <Threads entry={entry} stats={stats} statsError={statsError} />}
          <OnThisDay from={date} />
          {next && <NextDayCard date={next} label={formatLongDate(next)} />}
        </aside>
      </div>
    </>
  )
}

// ── The entry itself ────────────────────────────────────────────────────────

function EntryBody({
  entry,
  memos,
  memosError,
}: {
  entry: JournalEntry
  memos: JournalVoiceMemo[] | undefined
  memosError: unknown
}) {
  const paragraphs = paragraphsOf(entry.text)
  const at = (index: number) => (memos ?? []).filter((m) => m.paragraphIndex === index)
  // A memo whose position was never recorded lands after the text rather than at a guessed spot.
  const unplaced = (memos ?? []).filter(
    (m) => m.paragraphIndex === null || m.paragraphIndex >= paragraphs.length,
  )
  const anyMemo = (memos ?? []).length > 0

  return (
    <>
      <header className="flex flex-wrap items-baseline gap-[14px]">
        <h1 className="text-[34px] font-light leading-[1.1] tracking-[-0.025em]">
          {formatLongDate(entry.date)}
        </h1>
        <span className="text-[12px] font-medium leading-none text-microcopy">
          {entry.date.slice(0, 4)} · {partOfDay(entry.updatedAt)} · {entry.wordCount} words
        </span>
        <div className="ml-auto flex items-center gap-4">
          <ReadOnlyScale label="Mood" value={entry.mood} />
          <ReadOnlyScale label="Energy" value={entry.energy} />
        </div>
      </header>

      {entry.prompt && (
        <div className="mt-[18px] flex flex-wrap items-center gap-[9px] rounded-row border border-warning-line bg-prompt-fill px-[14px] py-[10px]">
          <Pencil size={15} strokeWidth={1.7} className="flex-none text-journal-deep" />
          <span className="text-[10.5px] font-semibold uppercase leading-none tracking-[0.06em] text-prompt-eyebrow">
            Prompt
          </span>
          <span className="text-[13.5px] font-medium leading-[1.3] text-ink-2">{entry.prompt}</span>
          <span className="ml-auto text-[10.5px] font-medium leading-none text-prompt-answered">
            answered
          </span>
        </div>
      )}

      {paragraphs.map((paragraph, i) => (
        <Fragment key={i}>
          <p
            className={`${i === 0 ? 'mt-5' : 'mt-4'} max-w-[820px] text-[17px] leading-[1.78] text-ink-2 text-pretty`}
          >
            {paragraph}
          </p>
          {at(i).map((memo) => (
            <DayMemo key={memo.id} memo={memo} />
          ))}
        </Fragment>
      ))}

      {unplaced.map((memo) => (
        <DayMemo key={memo.id} memo={memo} />
      ))}
      {anyMemo && <MemoAudioNote />}

      {/* Voice memos are live in both modes. This branch is a read that FAILED — and an entry
          that silently drops its memos is an entry that quietly loses part of the day, so the
          failure is stated rather than rendered as "no memos". */}
      {memosError && (
        <p className="mt-4 text-[11.5px] leading-[1.5] text-microcopy text-pretty">
          {memosError instanceof Error
            ? memosError.message
            : 'Voice memos for this day could not be read.'}{' '}
          The written entry above is complete.
        </p>
      )}

      <footer className="mt-auto flex flex-wrap items-center gap-[9px] pt-5">
        {entry.tags.map((tag) => (
          <span
            key={tag}
            className="rounded-pill border border-line-3 bg-surface-4 px-3 py-[6px] text-[11.5px] font-medium leading-[1.3] text-ink-2"
          >
            #{tag}
          </span>
        ))}
        {/* LIVE, and the one control on this reading surface — which is a deliberate line, not an
            inconsistency with the read-only mood and energy dots above. Mood and energy are a
            RECORD of how the day felt, taken at the time; setting them here would be answering a
            question the day never asked. A tag is FILING, and filing is something you do to an
            old entry by nature — you find out in November which September day the thought was in.
            It appends to the entry's stored `tags`; it changes no word of what was written. */}
        <AddTagChip date={entry.date} tags={entry.tags} />
        <span className="ml-auto text-[11.5px] leading-none text-faint">
          Reading view — writing happens on{' '}
          <Link
            to="/"
            className="text-muted underline underline-offset-2 transition-colors duration-[var(--dur-state)] ease-[var(--ease-ink)] hover:text-ink"
          >
            Today
          </Link>
          .
        </span>
      </footer>
    </>
  )
}

/**
 * Mood and energy on a reading surface are a TRACE, not a control. They are rendered as the same
 * five dots the composer uses and nothing more — the composer is where they get set, and a
 * clickable dot here would be editing history from a reading surface.
 */
function ReadOnlyScale({ label, value }: { label: string; value: number | null }) {
  return (
    <span className="flex items-center gap-2">
      <span className="text-[9.5px] font-medium uppercase leading-none tracking-[0.05em] text-faintest">
        {label}
      </span>
      {value === null ? (
        <span className="text-[10.5px] leading-none text-faintest">not set</span>
      ) : (
        <MoodDots value={value} size={8} />
      )}
    </span>
  )
}

/** Same as the Today composer's: long enough not to write per word, short enough to lose nothing. */
const AUTOSAVE_MS = 700

// ── The states that are not a written day ───────────────────────────────────

/**
 * WRITING STARTS HERE NOW (owner, 2026-09-01): "make it actually just open into a text editor
 * right there ... I don't want it to take me back to the home screen."
 *
 * TODAY ONLY. The copy below calls the archive a record and means it; a past day that could be
 * filled in from a reading surface would make every empty day ambiguous — never written, or
 * written later and backdated? A past day still offers TODAY's entry, which is the thing that can
 * honestly be written now, and that one is still a link because it genuinely goes elsewhere.
 */
function NoEntry({
  date,
  isToday,
  hasClock,
  onWrite,
}: {
  date: ISODate
  isToday: boolean
  hasClock: boolean
  onWrite: () => void
}) {
  return (
    <div className="max-w-[620px]">
      <h1 className="text-[34px] font-light leading-[1.1] tracking-[-0.025em]">
        {formatLongDate(date)}
      </h1>
      <p className="mt-4 text-body text-ink-3 text-pretty">
        Nothing was written on this day. That is the record, not a gap in it — the archive keeps
        the days you missed visible on purpose.
      </p>
      {hasClock &&
        (isToday ? (
          <button
            type="button"
            onClick={onWrite}
            className="mt-5 inline-block rounded-button bg-ink px-4 py-[9px] text-[11.5px] font-semibold leading-none text-surface transition-opacity duration-[var(--dur-state)] ease-[var(--ease-ink)] hover:opacity-90"
          >
            Write today
          </button>
        ) : (
          <Link
            to="/"
            className="mt-5 inline-block rounded-button bg-ink px-4 py-[9px] text-[11.5px] font-semibold leading-none text-surface transition-opacity duration-[var(--dur-state)] ease-[var(--ease-ink)] hover:opacity-90"
          >
            Write today’s entry
          </Link>
        ))}
    </div>
  )
}

/**
 * THE DAY PAGE'S OWN COMPOSER — the same contract as the Today card, on the reading surface.
 *
 * WHY NOT `JournalEntryCard`. That component is the Today screen's whole left column: the rotating
 * prompt, the mood and energy rows, the voice-memo column beside it. The owner asked for this one
 * BECAUSE it is plain ("I like this simplified look"), so bringing the card here would be
 * answering a request for less with more.
 *
 * THE UNDO CONTRACT (CLAUDE.md — no gate can see this). The textarea is UNCONTROLLED
 * (`defaultValue`, never `value`). A controlled input whose value is re-set by an autosave round
 * trip destroys the browser's native undo stack after the first save. If this ever becomes
 * controlled, check by hand: type, wait for the chip, press Ctrl+Z.
 *
 * THE DEBOUNCE IS STILL THIS FILE'S OWN (JournalEntryCard owns the original, Scratchpad has a
 * localStorage variant) — they differ in timing and in what leaving the screen means, and a
 * shared editor would be a refactor of the undo contract rather than an addition beside it.
 *
 * WHAT IS SHARED, as of JRN-1d, is the part that must never differ: `lib/use-journal-draft.ts`
 * keeps the words on disk from the first keystroke, puts them back at mount when the server
 * never got them, and re-sends them when the machine is online again. This page was the third
 * copy that hook was extracted to prevent.
 */
function DayComposer({ date, initial }: { date: ISODate; initial: string }) {
  const save = useSaveJournalEntry(date)
  // Read ONCE, for the uncontrolled box's starting value. Re-reading on every render would fight
  // the day's own autosave echo for control of the text being typed.
  const [openedWith] = useState(initial)
  const [words, setWords] = useState(() => (initial.trim() ? initial.trim().split(/\s+/).length : 0))
  const [saved, setSaved] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle')
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pending = useRef<{ date: ISODate; text: string } | null>(null)
  const field = useRef<HTMLTextAreaElement>(null)

  // ── THE DURABLE DRAFT (JRN-1d) ─────────────────────────────────────
  //
  // `settled` is unconditionally true: this component is only rendered once the day's read has
  // landed (see the parent), so there is no in-flight window to wait out. `readFailed` is
  // likewise absent — a failed read renders `ReadFailure` instead of this composer, so there is
  // no way to type blind here.
  const draft = useJournalDraft({
    date,
    serverText: initial,
    settled: true,
    busy: save.isPending || pending.current !== null,
    send: (text) => commit(text),
  })
  const { restored, unconfirmed, kept, autoRetries, wouldReplace } = draft

  useEffect(() => {
    if (restored) setWords(restored.text.trim() ? restored.text.trim().split(/\s+/).length : 0)
  }, [restored])

  // The same keystroke promise the Today composer makes: a debounce is a window in which the text
  // exists only in the DOM, and leaving the screen lands inside it eventually.
  const flushRef = useRef<() => void>(() => {})
  flushRef.current = () => {
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = null
    }
    const write = pending.current
    if (!write) return
    pending.current = null
    save.mutate(write, {
      onSuccess: () => setSaved('saved'),
      onError: () => setSaved('failed'),
    })
  }

  useEffect(() => {
    const flush = () => flushRef.current()
    const onHidden = () => {
      if (document.visibilityState === 'hidden') flush()
    }
    document.addEventListener('visibilitychange', onHidden)
    window.addEventListener('pagehide', flush)
    return () => {
      document.removeEventListener('visibilitychange', onHidden)
      window.removeEventListener('pagehide', flush)
      flush() // leaving the screen counts as leaving too
    }
  }, [])

  /** One save. The draft is cleared by the mutation itself, never by this callback. */
  function commit(text: string) {
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = null
    }
    pending.current = null
    setSaved('saving')
    save.mutate(
      { date, text },
      {
        onSuccess: (entry) => {
          draft.noteSaved(entry.text)
          setSaved('saved')
        },
        // The text is still in the textarea — it is uncontrolled, so a failed save cannot
        // erase it, and since JRN-1d it is on disk as well. What must not happen is the page
        // claiming "Saved" when nothing was.
        onError: () => setSaved('failed'),
      },
    )
  }

  function onType(text: string) {
    setWords(text.trim() ? text.trim().split(/\s+/).length : 0)
    // DURABLE FIRST, AND SYNCHRONOUSLY — before the debounce and before the request, because
    // everything after this line can be interrupted and this line cannot.
    draft.noteKeystroke(text)
    setSaved('saving')
    pending.current = { date, text }
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      timer.current = null
      pending.current = null
      commit(text)
    }, AUTOSAVE_MS)
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-none items-baseline gap-[10px]">
        <h1 className="text-[34px] font-light leading-[1.1] tracking-[-0.025em]">
          {formatLongDate(date)}
        </h1>
        {/* THE CHIP NEVER CLAIMS MORE THAN IT KNOWS. A stored draft outranks both 'saved' and
            'idle': the optimistic write in useSaveJournalEntry moves the cache BEFORE the
            request, so without this the page could say "Saved" over words the server refused. */}
        {unconfirmed || saved === 'failed' ? (
          <span className="text-[11px] font-semibold leading-none text-danger">
            {saved === 'saving' ? 'Saving…' : unsavedSentence(kept, autoRetries, 'in this browser')}
          </span>
        ) : (
          saved !== 'idle' && (
            <span className="text-[11px] font-semibold leading-none text-success">
              {saved === 'saving' ? 'Saving…' : 'Saved'}
            </span>
          )
        )}
      </div>

      {/* THE WORDS THIS BROWSER KEPT. Shown only when a draft was put back and the server has
          still not confirmed it: one sentence about where the words are, and one button that
          sends them. */}
      {restored && unconfirmed && (
        <div className="mt-4 max-w-[620px] rounded-row border border-warning-line bg-warning-fill px-4 py-3">
          <p className="text-body-sm text-warning-2 text-pretty">
            Not saved. These words were kept in this browser
            {restored.typedAt.includes('T') ? ` from ${clockFromTimestamp(restored.typedAt)}` : ''}{' '}
            and put back here.{' '}
            {wouldReplace
              ? 'This day’s saved entry is different from them, so sending replaces what is saved — Retry is the only thing that sends them.'
              : 'Sending them keeps everything that is already saved, and they go up on their own once you are back online.'}
          </p>
          <button
            type="button"
            onClick={() => commit(field.current?.value ?? restored.text)}
            className="mt-2 rounded-button bg-ink px-3 py-[6px] text-[11.5px] font-semibold leading-none text-surface transition-opacity duration-[var(--dur-state)] ease-[var(--ease-ink)] hover:opacity-90"
          >
            Retry
          </button>
        </div>
      )}

      {/* Wrapped in the journal composer's own field class (see JournalEntryCard), not left as a
          bare `outline-none` box: 12d's text-entry rule applies here too, and without the
          wrapper there is no focus indicator at all — an accessibility regression, not a style
          miss. */}
      <div className="mo-field-journal mt-5 min-h-[220px] w-full flex-1">
        <textarea
          autoFocus
          ref={field}
          // The key changes on a new DATE and once when a restored draft is put back — never
          // again after that, which is what keeps the undo stack alive.
          key={draft.fieldKey}
          defaultValue={restored?.text ?? openedWith}
          onChange={(event) => onType(event.target.value)}
          placeholder="Write as much or as little as you want. Nothing here is graded."
          aria-label={`Journal entry for ${formatLongDate(date)}`}
          className="size-full resize-none border-0 bg-transparent p-0 text-[16.5px] leading-[1.72] text-ink-2 outline-none placeholder:font-normal placeholder:text-microcopy text-pretty"
        />
      </div>

      <div className="mt-4 flex flex-none flex-wrap items-center gap-x-4 gap-y-2">
        <span className="rounded-pill bg-fill-2 px-3 py-[6px] text-[11.5px] font-medium leading-[1.3] text-ink-2">
          {words} {words === 1 ? 'word' : 'words'}
        </span>
        {/* The prompt, mood and energy live on Today. Saying so beats a reader wondering whether
            this box quietly drops them. */}
        <span className="text-[11.5px] leading-[1.45] text-microcopy text-pretty">
          Saves to this day. The prompt, mood and energy are on Today.
        </span>
      </div>
    </div>
  )
}

function ReadFailure({ date, message }: { date: ISODate; message: string }) {
  return (
    <div className="max-w-[620px]">
      <h1 className="text-[34px] font-light leading-[1.1] tracking-[-0.025em]">
        {formatLongDate(date)}
      </h1>
      {/* An empty page here would read as "you wrote nothing", which is a different and worse
          claim than "this could not be read". */}
      <p className="mt-4 text-body text-ink-3 text-pretty">
        This day could not be loaded, so it is not shown. Whatever is saved is untouched.
      </p>
      {message && <p className="mt-2 text-body-sm text-microcopy text-pretty">{message}</p>}
      <Link
        to="/journal"
        className="mt-5 inline-block rounded-button bg-ink px-4 py-[9px] text-[11.5px] font-semibold leading-none text-surface transition-opacity duration-[var(--dur-state)] ease-[var(--ease-ink)] hover:opacity-90"
      >
        Back to the archive
      </Link>
    </div>
  )
}

function EntrySkeleton() {
  return (
    <div>
      <span className="sr-only">Loading…</span>
      <div className="h-[30px] w-[320px] rounded-[6px] bg-fill" aria-hidden />
      <div className="mt-[22px] h-[38px] w-full max-w-[820px] rounded-row bg-fill" aria-hidden />
      <div className="mt-6 flex max-w-[820px] flex-col gap-[10px]" aria-hidden>
        <div className="h-[12px] w-full rounded-full bg-line-3" />
        <div className="h-[12px] w-full rounded-full bg-line-3" />
        <div className="h-[12px] w-[64%] rounded-full bg-line-3" />
      </div>
      <div className="mt-5 h-[76px] max-w-[820px] rounded-panel bg-surface-2" aria-hidden />
    </div>
  )
}

// ── Band pieces ─────────────────────────────────────────────────────────────

/** 'Sat 15' — the label the prev/next buttons carry. */
const stepLabel = (date: ISODate) => `${weekdayName(date, true)} ${dayOfMonth(date)}`

function StepLink({
  to,
  label,
  direction,
}: {
  to: string
  label: string
  direction: 'back' | 'forward'
}) {
  return (
    <Link
      to={to}
      className="flex items-center gap-2 rounded-button border border-line bg-surface px-3 py-[7px] transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-surface-4"
    >
      {direction === 'back' && <ChevronLeft size={13} className="text-ink-2" />}
      <span className="text-[11.5px] font-medium leading-none text-ink-2">{label}</span>
      {direction === 'forward' && <ChevronRight size={13} className="text-ink-2" />}
    </Link>
  )
}

/**
 * The mockup's chip reads "Saved · edits go straight in". This one reports what it actually
 * knows — when the day was last written — and never claims an edit path this page does not have.
 */
function SavedChip({ entry }: { entry: JournalEntry | null | undefined }) {
  if (!entry || entry.text.trim().length === 0) return null
  return (
    <span className="rounded-pill border border-line bg-surface px-[9px] py-1 text-[10.5px] font-medium leading-[1.4] text-ink-2">
      Saved · {partOfDay(entry.updatedAt)} of {formatShortDateWithYear(entry.updatedAt.slice(0, 10) as ISODate)}
    </span>
  )
}
